import React, {useEffect, useState} from "react"
import {interval, animationFrameScheduler} from "rxjs"
import {map} from "rxjs/operators"
import {takeUntil, repeat} from "rxjs/operators"
import {useTheme} from "@/hooks/useTheme"

const Donut = ({count, percentComplete}) => {
  const theme = useTheme()

  const size = 192
  const radius = 42
  const strokeWidth = 6
  const fontSize = 22
  const viewBox = `0 0 ${size} ${size}`
  const center = size / 2
  const circumference = 2 * Math.PI * radius
  const dashOffset = circumference * 0.25

  return (
    <svg width={size} height={size} viewBox={viewBox} className="donut">
      <circle className="donut-hole"
              cx={center}
              cy={center}
              r={radius}
              fill={theme.backgroundColor1}/>

      <circle className="donut-ring"
              cx={center}
              cy={center}
              r={radius}
              fill="transparent"
              stroke={theme.borderColor1}
              strokeWidth={strokeWidth}/>

      <circle className="donut-segment"
              cx={center}
              cy={center}
              r={radius}
              fill="transparent"
              stroke={theme.brand.primary}
              strokeWidth={strokeWidth}
              strokeDasharray={`${percentComplete} ${(100 - percentComplete)}`}
              strokeDashoffset={dashOffset}/>

      <text className="text"
            textAnchor="middle"
            fontSize={fontSize}
            fill={theme.fontColor4}
            fontWeight={600}
            x="50%"
            y="60%">
        {count}
      </text>
    </svg>
  )
}

const useTimeCounter = (onResetCounter$) => {
  const [startTime, resetStartTime] = useState(Date.now())
  const [percentage, setPercentage] = useState(0)
  const [counter, setCounter] = useState(0)

  useEffect(() => {
    const refreshRate = 50
    const duration = 1000
    const totalIntervals = duration / refreshRate
    const timer$ = interval(refreshRate, animationFrameScheduler).pipe(
      map(counter => counter + 1),
      map(counter => counter / totalIntervals * 100),
      takeUntil(interval(duration + refreshRate)),
      repeat()
    )

    const subscriptions = []
    subscriptions.push(timer$
      .subscribe(percentage => {
        setPercentage(percentage)
        percentage === 100 && setCounter(Math.floor((Date.now() - startTime) / 1000))
      }))

    if (onResetCounter$) {
      subscriptions.push(onResetCounter$.subscribe(() => {
        resetStartTime(Date.now())
      }))
    }

    return () => {
      subscriptions.forEach(s => s.unsubscribe())
    }
  }, [onResetCounter$, startTime])

  return [percentage, counter]
}

function LoadingCounter({onResetCounter$, useCounter}) {
  const [percentage, counter] = useTimeCounter(onResetCounter$)
  return (
    <Donut
      count={useCounter === true ? counter : ""}
      percentComplete={percentage}
    />
  )
}

export default LoadingCounter
